import { Controller } from '@hotwired/stimulus';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

function safeFocus(element) {
  if (!element || typeof element.focus !== 'function') return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

export default class extends Controller {
  static targets = [
    'dockLabel',
    'dockCount',
    'segmentsTrack',
    'scoreBreakdown',
    'scoreHeadline',
    'scoreCorrect',
    'scoreWrong',
    'scoreMessage',
    'retakeBtn',
    'signInCta',
    'dock',
    'segment',
    'card',
    'feedback',
    'feedbackTitle'
  ];

  static values = {
    perfectScoreMessage: { type: String, default: '' },
    quizSlug: String,
    attemptsUrl: { type: String, default: '/quiz/attempts' },
    completionsUrl: { type: String, default: '/quiz/completions' },
    signedIn: { type: Boolean, default: false }
  };

  connect() {
    this.cards = this.cardTargets;
    if (!this.cards.length) return;

    this.total = this.cards.length;
    this.answered = 0;
    this.correctCount = 0;
    this.completionRecorded = false;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.cards.forEach((card) => {
      const correctIndex = parseInt(card.getAttribute('data-answer'), 10);
      const optionCount = card.querySelectorAll('.quiz-option').length;

      if (Number.isNaN(correctIndex) || correctIndex < 0 || correctIndex >= optionCount) {
        card.setAttribute('data-quiz-broken', 'true');
      }
    });

    this.boundClick = this.onClick.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundRetake = this.onRetake.bind(this);

    this.element.addEventListener('click', this.boundClick);
    this.element.addEventListener('keydown', this.boundKeydown);
    if (this.hasRetakeBtnTarget) {
      this.retakeBtnTarget.addEventListener('click', this.boundRetake);
    }

    this.updateDock();
  }

  disconnect() {
    this.element.removeEventListener('click', this.boundClick);
    this.element.removeEventListener('keydown', this.boundKeydown);
    if (this.hasRetakeBtnTarget) {
      this.retakeBtnTarget.removeEventListener('click', this.boundRetake);
    }
  }

  onClick(event) {
    const button = event.target.closest('.quiz-option');
    if (!button || !this.element.contains(button)) return;

    const card = button.closest('[data-quiz-question]');
    if (!card || card.getAttribute('data-quiz-broken') === 'true') return;
    if (card.classList.contains('is-answered') || button.disabled) return;

    const options = this.getCardOptions(card);
    const correctIndex = parseInt(card.getAttribute('data-answer'), 10);
    const selected = parseInt(button.getAttribute('data-option'), 10);

    if (Number.isNaN(selected) || selected < 0 || selected >= options.length) return;
    this.answerCard(card, selected, options, correctIndex);
  }

  onKeydown(event) {
    const button = event.target.closest('.quiz-option');
    if (!button || !this.element.contains(button)) return;

    const card = button.closest('[data-quiz-question]');
    if (!card || card.classList.contains('is-answered')) return;

    const key = event.key;
    if (key !== 'ArrowDown' && key !== 'ArrowUp') return;

    const options = this.getCardOptions(card);
    const index = options.indexOf(button);
    if (index < 0) return;

    event.preventDefault();
    const delta = key === 'ArrowUp' ? -1 : 1;
    const nextIndex = (index + delta + options.length) % options.length;
    const nextOption = options[nextIndex];
    if (nextOption && !nextOption.disabled) safeFocus(nextOption);
  }

  onRetake() {
    this.answered = 0;
    this.correctCount = 0;
    this.completionRecorded = false;

    this.cards.forEach((card) => this.resetCard(card));
    this.hideScoreBreakdown();
    this.updateDock();

    this.element.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
    const firstOption = this.cards[0]?.querySelector('.quiz-option');
    safeFocus(firstOption);
  }

  getCardOptions(card) {
    return Array.from(card.querySelectorAll('.quiz-option'));
  }

  resetCard(card) {
    card.classList.remove('is-answered', 'is-answered-wrong');

    card.querySelectorAll('.quiz-option').forEach((opt) => {
      opt.disabled = false;
      opt.setAttribute('aria-checked', 'false');
      opt.removeAttribute('aria-disabled');
      opt.classList.remove('is-correct', 'is-wrong', 'is-dimmed');
    });

    const feedback = card.querySelector('[data-feedback]');
    if (!feedback) return;

    feedback.classList.add('hidden');
    feedback.classList.remove('is-correct', 'is-wrong', 'is-visible');
    feedback.style.opacity = '';
    const title = feedback.querySelector('[data-feedback-title]');
    if (title) title.textContent = '';
  }

  updateSegments() {
    this.cards.forEach((card, index) => {
      const questionId = card.getAttribute('data-quiz-question');
      const segment = this.element.querySelector(`[data-segment="${questionId}"]`);
      if (!segment) return;

      segment.classList.remove('is-current', 'is-correct', 'is-wrong', 'is-pending');

      if (card.classList.contains('is-answered')) {
        if (card.classList.contains('is-answered-wrong')) {
          segment.classList.add('is-wrong');
        } else {
          segment.classList.add('is-correct');
        }
      } else if (index === this.answered) {
        segment.classList.add('is-current');
      } else {
        segment.classList.add('is-pending');
      }
    });

    if (this.hasSegmentsTrackTarget) {
      this.segmentsTrackTarget.setAttribute('aria-valuenow', String(this.answered));
    }
  }

  updateDock() {
    if (this.hasDockCountTarget) {
      this.dockCountTarget.textContent = `${this.answered} / ${this.total}`;
    }

    if (this.answered < this.total && this.hasDockLabelTarget) {
      this.dockLabelTarget.textContent = `Question ${this.answered + 1} of ${this.total}`;
    }

    this.updateSegments();

    if (this.answered === this.total) {
      this.revealScoreBreakdown();
    }
  }

  revealScoreBreakdown() {
    const wrongCount = this.total - this.correctCount;

    if (this.hasDockLabelTarget) this.dockLabelTarget.textContent = 'Quiz complete';
    if (this.hasScoreHeadlineTarget) {
      this.scoreHeadlineTarget.textContent = this.scoreHeadline(this.correctCount, this.total);
    }
    if (this.hasScoreCorrectTarget) this.scoreCorrectTarget.textContent = String(this.correctCount);
    if (this.hasScoreWrongTarget) this.scoreWrongTarget.textContent = String(wrongCount);
    if (this.hasScoreMessageTarget) {
      this.scoreMessageTarget.textContent = this.scoreMessage(this.correctCount, this.total);
    }

    if (this.hasScoreBreakdownTarget) {
      this.scoreBreakdownTarget.classList.remove('hidden');
      if (!this.reduceMotion) this.scoreBreakdownTarget.classList.add('is-revealed');
    }
    if (this.hasRetakeBtnTarget) this.retakeBtnTarget.classList.remove('hidden');
    if (!this.signedInValue && this.hasSignInCtaTarget) {
      this.signInCtaTarget.classList.remove('hidden');
    }
    if (this.hasDockTarget) this.dockTarget.classList.add('is-complete');

    this.recordCompletion();

    if (this.hasDockTarget) {
      this.dockTarget.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'end' });
      safeFocus(this.dockTarget);
    }
  }

  hideScoreBreakdown() {
    if (this.hasScoreBreakdownTarget) {
      this.scoreBreakdownTarget.classList.add('hidden');
      this.scoreBreakdownTarget.classList.remove('is-revealed');
    }
    if (this.hasRetakeBtnTarget) this.retakeBtnTarget.classList.add('hidden');
    if (this.hasSignInCtaTarget) this.signInCtaTarget.classList.add('hidden');
    if (this.hasDockTarget) this.dockTarget.classList.remove('is-complete');
    if (this.hasScoreHeadlineTarget) this.scoreHeadlineTarget.textContent = '';
    if (this.hasScoreMessageTarget) this.scoreMessageTarget.textContent = '';
  }

  revealFeedback(feedback, isCorrect) {
    feedback.classList.remove('hidden');
    feedback.classList.add(isCorrect ? 'is-correct' : 'is-wrong');

    if (!this.reduceMotion) {
      feedback.style.opacity = '0';
      feedback.classList.add('is-visible');
      requestAnimationFrame(() => {
        feedback.style.opacity = '1';
      });
    } else {
      feedback.classList.add('is-visible');
    }
  }

  answerCard(card, selected, options, correctIndex) {
    const isCorrect = selected === correctIndex;
    const correctLetter = LETTERS[correctIndex] || String(correctIndex + 1);

    card.classList.add('is-answered');
    if (!isCorrect) card.classList.add('is-answered-wrong');

    this.answered += 1;
    if (isCorrect) this.correctCount += 1;

    options.forEach((opt, index) => {
      opt.disabled = true;
      opt.setAttribute('aria-disabled', 'true');
      opt.setAttribute('aria-checked', index === selected ? 'true' : 'false');

      if (index === correctIndex) {
        opt.classList.add('is-correct');
      } else if (index === selected && !isCorrect) {
        opt.classList.add('is-wrong');
      } else {
        opt.classList.add('is-dimmed');
      }
    });

    const feedback = card.querySelector('[data-feedback]');
    const feedbackTitle = card.querySelector('[data-feedback-title]');
    if (feedbackTitle) {
      feedbackTitle.textContent = isCorrect
        ? `Correct. The answer is ${correctLetter}.`
        : `Not quite. The answer is ${correctLetter}.`;
    }
    if (feedback) this.revealFeedback(feedback, isCorrect);
    this.recordAttempt(card, selected, isCorrect);
    this.updateDock();

    if (this.answered < this.total) {
      const nextCard = this.cards[this.answered];
      if (nextCard) {
        nextCard.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
    }
  }

  scoreHeadline(score, totalQuestions) {
    if (score === totalQuestions) return 'Perfect score!';
    if (score >= 3) return 'Pretty good!';
    return 'Keep practicing!';
  }

  scoreMessage(score, totalQuestions) {
    if (score === totalQuestions) {
      return this.perfectScoreMessageValue || 'You got every question right. Nice work!';
    }
    if (score >= 3) {
      return 'Scroll up to reread any explanations you missed.';
    }
    return 'Scroll up and study the ones you missed.';
  }

  recordAttempt(card, selectedIndex, correct) {
    const questionSlug = card.getAttribute('data-question-slug');
    if (!questionSlug || !this.hasQuizSlugValue) return;

    fetch(this.attemptsUrlValue, {
      method: 'POST',
      headers: this.jsonHeaders(),
      body: JSON.stringify({
        quiz_slug: this.quizSlugValue,
        question_slug: questionSlug,
        selected_index: selectedIndex
      }),
      credentials: 'same-origin'
    }).catch(() => {});
  }

  recordCompletion() {
    if (this.completionRecorded || !this.hasQuizSlugValue) return;

    this.completionRecorded = true;

    fetch(this.completionsUrlValue, {
      method: 'POST',
      headers: this.jsonHeaders(),
      body: JSON.stringify({
        quiz_slug: this.quizSlugValue,
        score: this.correctCount,
        total: this.total
      }),
      credentials: 'same-origin'
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data?.unlocked?.length) return;
        this.showAchievementUnlocks(data.unlocked);
      })
      .catch(() => {});
  }

  showAchievementUnlocks(unlockedKeys) {
    if (!this.hasScoreMessageTarget || !unlockedKeys.length) return;

    const labels = unlockedKeys.map((key) => this.achievementTitleForKey(key));
    const suffix = ` Achievement unlocked: ${labels.join(', ')}.`;
    const current = this.scoreMessageTarget.textContent || '';
    if (!current.includes('Achievement unlocked:')) {
      this.scoreMessageTarget.textContent = `${current}${suffix}`;
    }
  }

  achievementTitleForKey(key) {
    if (key === 'quiz_perfect:ultimate-sheets-quiz') return 'Ultimate Sheets Master';
    if (key === 'quiz_perfect:god-tier-quiz') return 'God Tier Genius';
    if (key.startsWith('quiz_perfect:')) return 'Perfect score';
    if (key.startsWith('quizzes_completed_')) return 'Quiz milestone';
    return 'Achievement';
  }

  jsonHeaders() {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': this.csrfToken()
    };
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
  }
}
